import axios from "axios";
import Authentication from "@/services/authentication";

// Note: deploy scripts will override this for PROD or DEV to api or apidev respectively
const API_PATH_OUTAGE= process.env.VUE_APP_API_PATH_OUTAGE;
const API_PATH_UTILITY = process.env.VUE_APP_API_PATH_UTILITY;
const API_PATH_UTILITY_TYPE = process.env.VUE_APP_API_PATH_UTILITY_TYPE;
const API_PATH_TOWN = process.env.VUE_APP_API_PATH_TOWN;
const API_PATH_COUNTY = process.env.VUE_APP_API_PATH_COUNTY;

const DEFAULT_UTILITY_TYPE = 'electric';
const CONTENT_TYPE_JSON_HEADER = { 'Content-Type': 'application/json' };
const CACHE_CONTROL_MAX_AGE_0_HEADER = { 'Cache-Control': 'max-age=0' };

export default class API {
  static token = null;

  // ----------------- Outage APIs ------------------------------------------------------------------------
  static async GetUtilityOutages(utilityType, setCacheControlMaxAgeHeader) {
    if(!utilityType) {
      utilityType = DEFAULT_UTILITY_TYPE;
    }

    const params = this._getCacheControlMaxAgeHeaderParams(setCacheControlMaxAgeHeader);

    return await this._doApiRequest(API_PATH_OUTAGE + `/utility?utilityType=${utilityType}`, params);
  }

  static async GetUtilityOutagesWithTowns(utilityType, setCacheControlMaxAgeHeader) {
    if(!utilityType) {
      utilityType = DEFAULT_UTILITY_TYPE;
    }

    const params = this._getCacheControlMaxAgeHeaderParams(setCacheControlMaxAgeHeader);

    return await this._doApiRequest(API_PATH_OUTAGE + `/utility?utilityType=${utilityType}&expand=town`, params);
  }

  static async GetUtilityOutagesWithCounties(utilityType, setCacheControlMaxAgeHeader) {
    if(!utilityType) {
      utilityType = DEFAULT_UTILITY_TYPE;
    }

    const params = this._getCacheControlMaxAgeHeaderParams(setCacheControlMaxAgeHeader);

    return await this._doApiRequest(API_PATH_OUTAGE + `/utility?expand=county`, params);
  }
    
  static async GetTownOutages(utilityType, setCacheControlMaxAgeHeader) {
    if(!utilityType) {
      utilityType = DEFAULT_UTILITY_TYPE;
    }

    const params = this._getCacheControlMaxAgeHeaderParams(setCacheControlMaxAgeHeader);

    return await this._doApiRequest(API_PATH_OUTAGE + `/town?utilityType=${utilityType}`, params);
  }

  static async GetTownOutagesWithUtilities(utilityType, setCacheControlMaxAgeHeader) {
    if(!utilityType) {
      utilityType = DEFAULT_UTILITY_TYPE;
    }
    const params = this._getCacheControlMaxAgeHeaderParams(setCacheControlMaxAgeHeader);

    return await this._doApiRequest(API_PATH_OUTAGE + `/town?utilityType=${utilityType}&expand=utility`, params);
  }
    
  static async GetCountyOutages(utilityType, setCacheControlMaxAgeHeader) {
    if(!utilityType) {
      utilityType = DEFAULT_UTILITY_TYPE;
    }

    const params = this._getCacheControlMaxAgeHeaderParams(setCacheControlMaxAgeHeader);

    return await this._doApiRequest(API_PATH_OUTAGE + `/county?utilityType=${utilityType}`, params);
  }

  static async GetCountyOutagesWithUtilities(utilityType, setCacheControlMaxAgeHeader) {
    if(!utilityType) {
      utilityType = DEFAULT_UTILITY_TYPE;
    }

    const params = this._getCacheControlMaxAgeHeaderParams(setCacheControlMaxAgeHeader);

    return await this._doApiRequest(API_PATH_OUTAGE + `/county?utilityType=${utilityType}&expand=utility`, params);
  }

  static async GetOutagesHistory(startDate, endDate, setCacheControlMaxAgeHeader) {
    const params = this._getCacheControlMaxAgeHeaderParams(setCacheControlMaxAgeHeader);

    return await this._doApiRequest(API_PATH_OUTAGE + `/history?startDate=${startDate}&endDate=${endDate}`, params);
  }

  static async UpdateUtilityOutages(postData, utilityCode) {
    return await this._doApiRequest(`${API_PATH_OUTAGE}?utilityCode=${utilityCode}`, { method: 'post', auth: true, postData: postData, headers: CONTENT_TYPE_JSON_HEADER });
  }

  // ----------------- Utility APIs ------------------------------------------------------------------------
  static async GetUtilites() {
    return await this._doApiRequest(API_PATH_UTILITY + '?expand=town');
  }

  static async GetUtilitesForMe() {
    return await this._doApiRequest(API_PATH_UTILITY + '/me?expand=town', {auth: true});
  }

  static async CreateUtility(postData) {
    return await this._doApiRequest(`${API_PATH_UTILITY}`, { method: 'post', auth: true, postData: postData, headers: CONTENT_TYPE_JSON_HEADER });
  }

  static async UpdateUtility(postData) {
    return await this._doApiRequest(`${API_PATH_UTILITY}/${postData.utilityId}`, { method: 'post', auth: true, postData: postData, headers: CONTENT_TYPE_JSON_HEADER });
  }

  static async RemoveUtility(utilityId) {
    return await this._doApiRequest(`${API_PATH_UTILITY}/${utilityId}`, { method: 'delete', auth: true, headers: CONTENT_TYPE_JSON_HEADER });
  }

  static async AddTownToUtility(utilityId, townId) {
    return await this._doApiRequest(`${API_PATH_UTILITY}/${utilityId}/town/${townId}`, { method: 'post', auth: true, postData: null, headers: CONTENT_TYPE_JSON_HEADER });
  }

  static async RemoveTownFromUtility(utilityId, townId) {
    return await this._doApiRequest(`${API_PATH_UTILITY}/${utilityId}/town/${townId}`, { method: 'delete', auth: true, postData: null, headers: CONTENT_TYPE_JSON_HEADER });
  }

  // ----------------- Utility Type APIs ------------------------------------------------------------------------
  static async GetUtilityTypes() {
    return await this._doApiRequest(`${API_PATH_UTILITY_TYPE}`);
  }

  static async CreateUtilityType(postData) {
    return await this._doApiRequest(`${API_PATH_UTILITY_TYPE}`, { method: 'post', auth: true, postData: postData, headers: CONTENT_TYPE_JSON_HEADER });
  }

  static async UpdateUtilityType(postData) {
    return await this._doApiRequest(`${API_PATH_UTILITY_TYPE}/${postData.utilityTypeId}`, { method: 'post', auth: true, postData: postData, headers: CONTENT_TYPE_JSON_HEADER });
  }

  static async RemoveUtilityType(utilityTypeId) {
    return await this._doApiRequest(`${API_PATH_UTILITY_TYPE}/${utilityTypeId}`, { method: 'delete', auth: true, postData: null, headers: CONTENT_TYPE_JSON_HEADER });
  }

  // ----------------- Town APIs ------------------------------------------------------------------------
  static async GetTowns() {
    return await this._doApiRequest(API_PATH_TOWN);
  }

  static async CreateTown(postData) {
    return await this._doApiRequest(`${API_PATH_TOWN}`, { method: 'post', auth: true, postData: postData, headers: CONTENT_TYPE_JSON_HEADER });
  }

  static async UpdateTown(postData) {
    return await this._doApiRequest(`${API_PATH_TOWN}/${postData.townId}`, { method: 'post', auth: true, postData: postData, headers: CONTENT_TYPE_JSON_HEADER });
  }

  static async DeleteTown(townId) {
    return await this._doApiRequest(`${API_PATH_TOWN}/${townId}`, { method: 'delete', auth: true, postData: undefined, headers: CONTENT_TYPE_JSON_HEADER });
  }

  static async GetTownAka(townId) {
    return await this._doApiRequest(`${API_PATH_TOWN}/${townId}/synonym`);
  }

  static async CreateTownAka(postData) {
    return await this._doApiRequest(`${API_PATH_TOWN}/${postData.townId}/synonym`, { method: 'post', auth: true, postData: postData, headers: CONTENT_TYPE_JSON_HEADER });
  }

  static async UpdateTownAka(postData) {
    return await this._doApiRequest(`${API_PATH_TOWN}/${postData.townId}/synonym/${postData.townAkaId}`, { method: 'post', auth: true, postData: postData, headers: CONTENT_TYPE_JSON_HEADER });
  }

  static async DeleteTownAka(postData) {
    return await this._doApiRequest(`${API_PATH_TOWN}/${postData.townId}/synonym/${postData.townAkaId}`, { method: 'delete', auth: true, postData: postData, headers: CONTENT_TYPE_JSON_HEADER });
  }

  // ----------------- County APIs ------------------------------------------------------------------------
  static async GetCounties() {
    return await this._doApiRequest(API_PATH_COUNTY);
  }

  static async CreateCounty(postData) {
    return await this._doApiRequest(`${API_PATH_COUNTY}`, { method: 'post', auth: true, postData: postData, headers: CONTENT_TYPE_JSON_HEADER });
  }

  static async UpdateCounty(postData) {
    return await this._doApiRequest(`${API_PATH_COUNTY}/${postData.countyId}`, { method: 'post', auth: true, postData: postData, headers: CONTENT_TYPE_JSON_HEADER });
  }

  static async DeleteCounty(countyId) {
    return await this._doApiRequest(`${API_PATH_COUNTY}/${countyId}`, { method: 'delete', auth: true, postData: undefined, headers: CONTENT_TYPE_JSON_HEADER });
  }


  static async _doApiRequest(url, params) {
    params = params || {};
    console.log('======== doApiRequest =======');
    console.log(url);
    console.log(JSON.stringify(params));
    const method = params.method || 'get';
    const auth = params.auth || false;
    const postData = params.postData || undefined;
    const headers = params.headers || {};

    let config = {
      url: url,
      method: method,
      headers: headers
    };

    if(auth) {
      config.headers.Authorization = 'Bearer ' + await this._getAccessToken();
    }

    if(postData) {
      config.data = postData;
    }

    let response = await axios(config);

    return response.data;
  }

  static async _getAccessToken() {
    try {
      let user = await Authentication.getCurrentUser();

      return user.signInUserSession.accessToken.jwtToken;
    } catch (e) {
      console.log('User not signed in');
      console.log(e);
    }

    return '';
  }

  static _getCacheControlMaxAgeHeaderParams(setCacheControlMaxAgeHeader) {
    if(setCacheControlMaxAgeHeader !== undefined && setCacheControlMaxAgeHeader) {
      return {headers: CACHE_CONTROL_MAX_AGE_0_HEADER};
    }

    return null;
  }
}